import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

function fmt(n, opts = {}) {
  return new Intl.NumberFormat('en-US', opts).format(n);
}
function fmtMoney(n) {
  return '$' + fmt(Math.round(n));
}

export default function CalculatorSection() {
  const [visitors, setVisitors] = useState(250000);
  const [aov, setAov] = useState(120);
  const [abandon, setAbandon] = useState(70);
  const [cpm, setCpm] = useState(4.5);

  const projection = useMemo(() => {
    const ab = abandon / 100;
    const firstVisitConverters = visitors * 0.02;
    const potentialBuyers = visitors * ab * 0.02;
    const recoveredOrders = Math.max(0, Math.round(potentialBuyers * 0.15 + (visitors - firstVisitConverters) * 0.002));
    const recoveredRevenue = recoveredOrders * aov;
    const customerPays = recoveredRevenue * 0.12;
    const roas = customerPays > 0 ? (recoveredRevenue / customerPays).toFixed(1) : '0.0';
    return { recoveredRevenue, recoveredOrders, customerPays, roas };
  }, [visitors, aov, abandon, cpm]);

  return (
    <section className="py-12 lg:py-16 bg-[#262626] relative overflow-hidden" id="calculator" data-testid="calculator-section">
      <div className="highlights-glow highlights-glow-one" />
      <div className="highlights-glow highlights-glow-two" />
      <div className="container-r360">
        <div className="highlights-kicker text-white">
          <span />INTERACTIVE CALCULATOR
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-10 md:gap-6 items-start">
          <h2 className="reveal-on-scroll lg:col-span-7 font-display text-[3.25rem] sm:text-[4.5rem] xl:text-[5.75rem] font-bold leading-[0.92] tracking-[-0.03em]">
            What We'd <span className="accent">Recover</span> For You.
          </h2>
          <div className="lg:col-span-5">
            <p className="reveal-on-scroll leading-relaxed sec-subhead text-white">
              Discover your monthly revenue recovery potential using the same performance-driven CPS economics behind Retarget360.ai campaigns. See how much lost revenue you could reclaim with a smarter, risk-free retargeting model built for measurable growth.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
          <div className="space-y-10">
            <SliderRow
              label="Monthly website visitors"
              value={fmt(visitors)}
              min={10000}
              max={2000000}
              step={5000}
              val={visitors}
              onChange={setVisitors}
              minLabel="10,000"
              maxLabel="2,000,000"
              testid="slider-visitors"
            />
            <SliderRow
              label="Average order value ($)"
              value={`$${aov}`}
              min={10}
              max={2000}
              step={5}
              val={aov}
              onChange={setAov}
              minLabel="$10"
              maxLabel="$2,000"
              testid="slider-aov"
            />
            <SliderRow
              label="Cart abandonment rate"
              value={`${abandon}%`}
              min={30}
              max={95}
              step={1}
              val={abandon}
              onChange={setAbandon}
              minLabel="30%"
              maxLabel="95%"
              testid="slider-abandon"
            />
            <SliderRow
              label="Current CPM ($)"
              value={`$${cpm.toFixed(2)}`}
              min={1}
              max={25}
              step={0.5}
              val={cpm}
              onChange={(v) => setCpm(parseFloat(v))}
              minLabel="$1.00"
              maxLabel="$25.00"
              testid="slider-cpm"
            />
          </div>

          <div className="bg-[#0b0b0b] py-10 p-5 xl:p-10" data-testid="calc-result">
            <div className="font-bold mb-4 xl:mb-6">Projected Monthly Recovery · Retarget360 CPS Model</div>
            <div className="font-display text-[2.8rem] md:text-[3.75rem] xl:text-[4.75rem] font-extrabold text-green leading-none" data-testid="calc-revenue">
              {fmtMoney(projection.recoveredRevenue)}
            </div>
            <div className="font-bold text-[14px] uppercase mt-3">Recovered Revenue Per Month</div>

            <div className="grid sm:grid-cols-3 gap-6 sm:gap-2 xl:gap-4 mt-8 lg:mt-10">
              <div className="flex h-full flex-col">
                <div className="font-mono text-blue text-[13px] mb-2">PROJECTED ROAS</div>
                <div className="mt-auto font-display text-[30px] xl:text-[36px] font-extrabold leading-none">{projection.roas}×</div>
              </div>
              <div className="flex h-full flex-col">
                <div className="font-mono text-blue text-[13px] mb-2">RECOVERED ORDER (MO)</div>
                <div className="mt-auto font-display text-[30px] xl:text-[36px] font-extrabold leading-none">{fmt(projection.recoveredOrders)}</div>
              </div>
              <div className="flex h-full flex-col">
                <div className="font-mono text-blue text-[13px] mb-2">YOU PAY (CPS ONLY)</div>
                <div className="mt-auto font-display text-[30px] xl:text-[36px] font-extrabold leading-none">{fmtMoney(projection.customerPays)}</div>
              </div>
            </div>

            <div className="mt-8 border-t border-[#8a8a8a] pt-6">
              <div className="font-mono text-blue text-[13px] mb-1">CPM SPEND AVOIDED</div>
              <div className="font-display text-[30px] xl:text-[36px] font-extrabold">$0</div>
            </div>

            <div className="mt-6 lg:mt-8 flex flex-col items-start gap-4 lg:gap-6">
              <Link to="/book-a-demo" className="btn btn-primary" data-testid="calc-book-btn">
                Book a custom audit →
              </Link>
              <span className="font-mono text-[13px] uppercase tracking-[0.5px]">Model assumes industry-avg 2.8% CPS. Actual Deal will be Negotiated.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, min, max, step, val, onChange, minLabel, maxLabel, testid }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-4">
        <div className="text-[15px] font-bold">{label}</div>
        <div className="font-display text-[24px] font-bold">{value}</div>
      </div>
      <input
        className="r360-range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={val}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={testid}
      />
      <div className="flex justify-between mt-2 text-[13px] font-mono text-[#f0f0f0]">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
}
